import cx from 'classnames';
import _ from 'lodash';
import Papa from 'papaparse';
import ParticipantsTableInputRow from 'users/components/ParticipantsTableInputRow.jsx';
import React, { Component } from 'react';
// TODO: referencing non-component stylesheet
import 'components/styles/TableInput.scss';

const padRows = (rows, minimumRows) => {
  const filledRows = rows.filter(row => row);
  const emptyRowCount = minimumRows - filledRows.length;
  _.times(emptyRowCount, () => rows.push({ name: '', uid: '' }));
  return rows;
};

class ParticipantsTableInput extends Component {
  static propTypes = {
    groupId: React.PropTypes.number.isRequired,
    onChangeRow: React.PropTypes.func,
    initialRows: React.PropTypes.array,
    minimumRows: React.PropTypes.number,
    maximumRows: React.PropTypes.number,
    supportSms: React.PropTypes.bool,
    scroll: React.PropTypes.bool,
    placeholders: React.PropTypes.arrayOf(React.PropTypes.string),
    namePlaceholders: React.PropTypes.arrayOf(React.PropTypes.string),
    uidColumnHeader: React.PropTypes.string,
    showColumnHeaders: React.PropTypes.bool,
  };

  static defaultProps = {
    initialRows: [],
    minimumRows: 5,
    scroll: true,
    showColumnHeaders: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      rows: padRows(this.props.initialRows, this.props.minimumRows),
      shouldScroll: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    // So that setting initialRows in the parent of this component will
    // overwrite the rows stored in this component's state
    // in order to show server errors and remove rows
    if (JSON.stringify(this.state.rows) !== JSON.stringify(nextProps.initialRows)) {
      this.setState({ rows: padRows(nextProps.initialRows, this.props.minimumRows) });
    }
  }

  componentDidUpdate() {
    if (this.state.shouldScroll) {
      const { tableContainer } = this.refs;
      tableContainer.scrollTop = tableContainer.scrollHeight;
    }
  }

  onPaste = (e) => {
    let rows = [];
    let pastedText;

    try {
      pastedText = e.clipboardData.getData('text/plain');
    } catch (ex) {
      // IE 11 (but not Edge) requires 'Text' data type
      pastedText = e.clipboardData.getData('Text');
    }
    if (!pastedText) {
      return false;
    }

    Papa.parse(pastedText).data.forEach((row) => {
      const isSingleColumn = row.length === 1 && row[0].trim().length > 0;
      if (isSingleColumn) {
        // treat the values as uids
        rows.push(
          {
            name: '',
            uid: row[0].trim(),
            valid: false,
            pasted: true,
          });
      } else if (row[0] || row[1]) {
        rows.push(
          {
            name: row[0] ? row[0].trim() : '',
            uid: row[1] ? row[1].trim() : '',
            valid: false,
            pasted: true,
          });
      }
    });

    // If there are no rows or only a single value in a single row, use default paste behavior
    if (rows.length === 0 || (rows.length === 1 && (!rows[0].name || !rows[0].uid))) {
      return false;
    }

    rows = this.state.rows
      .filter(row => row.name || row.uid)
      .concat(rows.filter(row => row.name !== 'full name'));

    rows = _.uniqBy(rows, 'uid');

    // If the total is gte 5 we can display without scrolling
    // append an empty row
    if (rows.length >= this.props.minimumRows) {
      rows.push({
        name: '',
        uid: '',
      });
    }

    const paddedRows = padRows(rows, this.props.minimumRows);
    this.setState({ rows: paddedRows });
    if (this.props.onChangeRow) {
      this.props.onChangeRow(paddedRows);
    }
    return true;
  };

  onValidityChange = (rowIndex, valid) => {
    const rows = this.state.rows.slice(0);
    const row = rows[rowIndex];

    if (!row || !!row.valid === valid) {
      return;
    }

    if (row) {
      row.valid = valid;
      row.pasted = false;
    }

    this.setState({ rows });
    if (this.props.onChangeRow) {
      this.props.onChangeRow(rows);
    }
  };

  onChange = (rowIndex, name, event) => {
    const { rows } = this.state;
    const row = rows[rowIndex];

    row[name] = event.target.value;
    row.pasted = false;
    if (row.serverErrors) {
      delete row.serverErrors[name];
    }

    let shouldScroll = false;

    // remove empty row if over minimumRows
    const rowCount = this.state.rows.length;
    const { minimumRows, maximumRows } = this.props;

    if (rows.filter(r => r).length > minimumRows && !row.uid && !row.name) {
      delete rows[rowIndex];
    } else {
      if (rowIndex === (rowCount - 1)) {
        // If an edit is being made in the last row
        if (!maximumRows || (maximumRows && rowCount < maximumRows)) {
          rows.push({
            name: '',
            uid: '',
            valid: false,
          }); // Push another blank row
          shouldScroll = true;
        }
      }
    }

    const paddedRows = padRows(rows, this.props.minimumRows);

    this.setState({ paddedRows, shouldScroll });
    if (this.props.onChangeRow) {
      this.props.onChangeRow(paddedRows);
    }
  };

  render() {
    const { supportSms, uidColumnHeader, showColumnHeaders, placeholders } = this.props;

    const namePlaceholders = this.props.namePlaceholders || [
      'Jane Doe',
      'Alice Smith',
      'John Doe',
      'Bob Johnson',
      'Art Vandelay',
    ];

    let title;
    if (uidColumnHeader) {
      title = uidColumnHeader;
    } else {
      title = supportSms ?
        'Email address or phone number' :
        'Email address';
    }

    let uidPlaceholders;
    if (placeholders) {
      uidPlaceholders = placeholders;
    } else {
      uidPlaceholders = [
        'name@email.com',
      ];
      if (supportSms) {
        uidPlaceholders.push('(555) 555-5555');
      }
    }

    return (
      <div className={cx('TableInput', this.props.className)} onPaste={this.onPaste}>
        {
          showColumnHeaders &&
            <header className="TableInput-headerRow">
              <span className="TableInput-cell--validation TableInput-cell" />
              <div className="TableInput-headerRow-cell TableInput-cell">
                Full name
              </div>
              <div className="TableInput-headerRow-cell TableInput-cell">
                {title}
              </div>
            </header>
        }
        <div className={cx({ 'TableInput-container': this.props.scroll })} ref="tableContainer">
          <div className="TableInput-body">
            {this.state.rows.map((row, rowIndex) =>
            (
              <ParticipantsTableInputRow
                key={`row-${rowIndex}`}
                groupId={this.props.groupId}
                name={row.name.trim()}
                namePlaceholder={namePlaceholders[rowIndex % namePlaceholders.length]}
                uid={row.uid.trim()}
                uidPlaceholder={uidPlaceholders[rowIndex % uidPlaceholders.length]}
                validateInitialValues={row.pasted}
                onValidityChange={this.onValidityChange.bind(this, rowIndex)}
                onChange={this.onChange.bind(this, rowIndex)}
                serverErrors={row.serverErrors}
                parsePasteWith={this.onPaste}
                autofocus={rowIndex === 0}
              />
            )
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ParticipantsTableInput;



// WEBPACK FOOTER //
// ./src/users/components/ParticipantsTableInput.jsx